import { useState, useEffect, useCallback, useRef } from "react";
import {
  integrationHealthService,
  IntegrationType,
  ConnectionHealth,
  ConnectionStatus,
} from "@/services/integration-health.service";

interface IntegrationStatusHookReturn {
  health: ConnectionHealth | null;
  status: ConnectionStatus;
  isMonitoring: boolean;
  isLoading: boolean;
  error: string | null;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  forceCheck: () => Promise<void>;
  refreshHealth: () => Promise<void>;
}

/**
 * Hook para monitoramento de status de integrações
 */
export const useIntegrationStatus = (
  integrationType: IntegrationType,
  integrationId: string,
  options: {
    autoStart?: boolean;
    interval?: number;
    onHealthChange?: (health: ConnectionHealth) => void;
    onError?: (error: string) => void;
  } = {},
): IntegrationStatusHookReturn => {
  const {
    autoStart = true,
    interval = 30000, // 30 segundos
    onHealthChange,
    onError,
  } = options;

  const [health, setHealth] = useState<ConnectionHealth | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const healthRef = useRef(health);
  const onHealthChangeRef = useRef(onHealthChange);
  const onErrorRef = useRef(onError);
  const isMonitoringRef = useRef(isMonitoring);

  // Atualizar refs
  useEffect(() => {
    healthRef.current = health;
    onHealthChangeRef.current = onHealthChange;
    onErrorRef.current = onError;
    isMonitoringRef.current = isMonitoring;
  }, [health, onHealthChange, onError, isMonitoring]);

  // Listener para eventos de atualização de health
  useEffect(() => {
    const handleHealthUpdate = (event: CustomEvent) => {
      const { integrationId: eventIntegrationId, health: eventHealth } =
        event.detail;

      if (eventIntegrationId === integrationId) {
        setHealth(eventHealth);

        if (onHealthChangeRef.current) {
          onHealthChangeRef.current(eventHealth);
        }
      }
    };

    window.addEventListener(
      "integration-health-update",
      handleHealthUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "integration-health-update",
        handleHealthUpdate as EventListener,
      );
    };
  }, [integrationId]);

  // Carregar health inicial
  useEffect(() => {
    const loadInitialHealth = () => {
      try {
        const existingHealth =
          integrationHealthService.getHealth(integrationId);
        if (existingHealth) {
          setHealth(existingHealth);
        }
      } catch (error) {
        console.error("Erro ao carregar health inicial:", error);
      }
    };

    loadInitialHealth();
  }, [integrationId]);

  async function startMonitoring() {
    if (isMonitoringRef.current || !integrationType || !integrationId) return;

    try {
      setIsLoading(true);
      setError(null);
      setIsMonitoring(true);

      await integrationHealthService.monitorIntegration(
        integrationType,
        integrationId,
        interval,
      );

      console.log(
        `✅ Monitoramento iniciado para ${integrationType}:${integrationId}`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro ao iniciar monitoramento";
      setError(errorMessage);
      setIsMonitoring(false);

      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }

      console.error("Erro ao iniciar monitoramento:", err);
    } finally {
      setIsLoading(false);
    }
  }

  function stopMonitoring() {
    if (!isMonitoringRef.current || !integrationId) return;

    try {
      integrationHealthService.stopMonitoring(integrationId);
      setIsMonitoring(false);
      console.log(`⏹️ Monitoramento parado para ${integrationId}`);
    } catch (err) {
      console.error("Erro ao parar monitoramento:", err);
    }
  }

  // Auto-start monitoring
  useEffect(() => {
    if (autoStart && integrationType && integrationId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      startMonitoring();
    }

    return () => {
      if (isMonitoringRef.current) {
        stopMonitoring();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationType, integrationId, autoStart]);

  const forceCheck = useCallback(async () => {
    if (!integrationType || !integrationId) return;

    try {
      setIsLoading(true);
      setError(null);

      const freshHealth = await integrationHealthService.forceHealthCheck(
        integrationType,
        integrationId,
      );

      setHealth(freshHealth);

      if (onHealthChangeRef.current) {
        onHealthChangeRef.current(freshHealth);
      }

      console.log(
        `🔄 Health check forçado para ${integrationType}:${integrationId}`,
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro ao verificar saúde";
      setError(errorMessage);

      if (onErrorRef.current) {
        onErrorRef.current(errorMessage);
      }

      console.error("Erro no health check:", err);
    } finally {
      setIsLoading(false);
    }
  }, [integrationType, integrationId]);

  const refreshHealth = useCallback(async () => {
    await forceCheck();
  }, [forceCheck]);

  // Status derivado do health
  const status: ConnectionStatus = health?.status || "disconnected";

  return {
    health,
    status,
    isMonitoring,
    isLoading,
    error,
    startMonitoring,
    stopMonitoring,
    forceCheck,
    refreshHealth,
  };
};

interface MultipleIntegrationStatusHookReturn {
  statuses: Array<{
    integrationType: IntegrationType;
    integrationId: string;
    health: ConnectionHealth | null;
    status: ConnectionStatus;
  }>;
  overallHealth: {
    connectedCount: number;
    totalCount: number;
    averageScore: number;
    criticalIssues: number;
  };
  isMonitoring: boolean;
  isLoading: boolean;
  error: string | null;
  startAll: () => Promise<void>;
  stopAll: () => void;
  refreshAll: () => Promise<void>;
  getIntegrationStatus: (integrationId: string) =>
    | {
        integrationType: IntegrationType;
        integrationId: string;
        health: ConnectionHealth | null;
        status: ConnectionStatus;
      }
    | undefined;
}

/**
 * Hook para monitoramento de múltiplas integrações
 */
export const useMultipleIntegrationStatus = (
  integrations: Array<{
    type: IntegrationType;
    id: string;
  }>,
  options: {
    autoStart?: boolean;
    interval?: number;
    onGlobalHealthChange?: (healthMap: Map<string, ConnectionHealth>) => void;
  } = {},
): MultipleIntegrationStatusHookReturn => {
  const { autoStart = true, interval = 30000, onGlobalHealthChange } = options;

  const [globalHealth, setGlobalHealth] = useState<
    Map<string, ConnectionHealth>
  >(new Map());
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMonitoringRef = useRef(isMonitoring);

  useEffect(() => {
    isMonitoringRef.current = isMonitoring;
  }, [isMonitoring]);

  const startAll = useCallback(async () => {
    if (integrations.length === 0 || isMonitoringRef.current) return;

    try {
      setIsLoading(true);
      setError(null);
      setIsMonitoring(true);

      for (const integration of integrations) {
        await integrationHealthService.monitorIntegration(
          integration.type,
          integration.id,
          interval,
        );
      }

      // Carregar health inicial
      const healthMap = integrationHealthService.getAllHealth();
      setGlobalHealth(new Map(healthMap));

      if (onGlobalHealthChange) {
        onGlobalHealthChange(healthMap);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro no monitoramento global";
      setError(errorMessage);
      setIsMonitoring(false);
      console.error("Erro no monitoramento global:", err);
    } finally {
      setIsLoading(false);
    }
  }, [integrations, interval, onGlobalHealthChange]);

  const stopAll = useCallback(() => {
    if (!isMonitoringRef.current) return;

    try {
      integrations.forEach((integration) => {
        integrationHealthService.stopMonitoring(integration.id);
      });
      setIsMonitoring(false);
    } catch (err) {
      console.error("Erro ao parar monitoramento global:", err);
    }
  }, [integrations]);

  // Auto-start monitoring
  useEffect(() => {
    if (autoStart && integrations.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      startAll();
    }

    // Cleanup
    return () => {
      if (isMonitoringRef.current) {
        stopAll();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrations, autoStart]);

  // Listener global para atualizações
  useEffect(() => {
    const handleHealthUpdate = () => {
      const healthMap = integrationHealthService.getAllHealth();
      setGlobalHealth(new Map(healthMap));

      if (onGlobalHealthChange) {
        onGlobalHealthChange(healthMap);
      }
    };

    window.addEventListener("integration-health-update", handleHealthUpdate);

    return () => {
      window.removeEventListener(
        "integration-health-update",
        handleHealthUpdate,
      );
    };
  }, [onGlobalHealthChange]);

  const refreshAll = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      for (const integration of integrations) {
        await integrationHealthService.forceHealthCheck(
          integration.type,
          integration.id,
        );
      }

      // Atualizar health
      const healthMap = integrationHealthService.getAllHealth();
      setGlobalHealth(new Map(healthMap));

      console.log("🔄 Health check forçado para todas as integrações");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Erro no health check global";
      setError(errorMessage);
      console.error("Erro no health check global:", err);
    } finally {
      setIsLoading(false);
    }
  }, [integrations]);

  // Computar statuses das integrações
  const statuses = integrations.map((integration) => {
    const health = globalHealth.get(integration.id) || null;
    return {
      integrationType: integration.type,
      integrationId: integration.id,
      health,
      status: (health?.status || "disconnected") as ConnectionStatus,
    };
  });

  // Computar saúde geral
  const overallHealth = {
    connectedCount: statuses.filter((s) => s.status === "connected").length,
    totalCount: statuses.length,
    averageScore:
      statuses.filter((s) => s.health).length > 0
        ? Math.round(
            statuses
              .filter((s) => s.health)
              .reduce((sum, s) => sum + (s.health?.score || 0), 0) /
              statuses.filter((s) => s.health).length,
          )
        : 0,
    criticalIssues: statuses.reduce(
      (sum, s) =>
        sum +
        (s.health?.issues.filter((i) => i.severity === "critical").length || 0),
      0,
    ),
  };

  const getIntegrationStatus = useCallback(
    (integrationId: string) => {
      return statuses.find((s) => s.integrationId === integrationId);
    },
    [statuses],
  );

  return {
    statuses,
    overallHealth,
    isMonitoring,
    isLoading,
    error,
    startAll,
    stopAll,
    refreshAll,
    getIntegrationStatus,
  };
};
